import { useLazyQuery } from '@apollo/client'
import { SUBORDINATE_SEARCH } from './Queries'
import { SubordinateSearchQuery, SubordinateSearchQueryVariables } from '../../types'
import { useCallback, useState } from 'react'
import { useEmployeeSearchStore } from '../../modules/employees/EmployeeSearch/EmployeeSearchStore'

interface UseSubordinateWithSearchReturn {
  loadSubordinates: ReturnType<typeof useLazyQuery<SubordinateSearchQuery, SubordinateSearchQueryVariables>>[0]
  subordinates: SubordinateSearchQuery['subordinateSearch']['edges']
  loading: boolean
  loadMore: () => void
  hasMore: boolean
}

export const useSubordinateWithSearch = (): UseSubordinateWithSearchReturn => {
  const search = useEmployeeSearchStore((state) => state.search)

  const [allSubordinates, setAllSubordinates] = useState<SubordinateSearchQuery['subordinateSearch']['edges']>([])

  const [loadSubordinates, { data, loading, fetchMore }] = useLazyQuery<SubordinateSearchQuery, SubordinateSearchQueryVariables>(
    SUBORDINATE_SEARCH,
    {
      variables: { search, pagination: {} },
      onCompleted: (result) => {
        if (result.subordinateSearch) {
          setAllSubordinates(result.subordinateSearch.edges)
        }
      },
    },
  )

  const loadMore = useCallback(() => {
    if (data && data.subordinateSearch.hasNextPage) {
      fetchMore({
        variables: {
          pagination: {
            cursorId: data.subordinateSearch.endCursorId,
            cursorLegalFirstName: data.subordinateSearch.endCursorLegalFirstName,
            cursorLegalLastName: data.subordinateSearch.endCursorLegalLastName,
          },
          search,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return previousResult
          }

          const previousEdges = previousResult.subordinateSearch.edges
          const fetchMoreEdges = fetchMoreResult.subordinateSearch.edges
          const combinedEdges = [...previousEdges, ...fetchMoreEdges]

          setAllSubordinates(combinedEdges)

          return {
            ...fetchMoreResult,
            subordinateSearch: {
              ...fetchMoreResult.subordinateSearch,
              edges: combinedEdges,
            },
          }
        },
      })
    }
  }, [data, fetchMore, search])

  return {
    loadSubordinates: loadSubordinates,
    subordinates: allSubordinates,
    loading: loading,
    loadMore,
    hasMore: data?.subordinateSearch?.hasNextPage ?? false,
  }
}
